import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { evaluateSchoolSubscription } from '../../lib/subscription';
import { savePersistedLogin, isTeacherOrStudentRole } from '../../lib/persistedAuth';
import './PasswordVerification.css';
import Logo from '../../assets/Logo.svg';

const PasswordVerification = () => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingLogin, setPendingLogin] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Get pending login data from sessionStorage
    const pendingLoginStr = sessionStorage.getItem('pendingLogin');
    if (!pendingLoginStr) {
      navigate('/');
      return;
    }

    try {
      const loginData = JSON.parse(pendingLoginStr);
      setPendingLogin(loginData);
      setUserEmail(loginData.userData.email);
    } catch (err) {navigate('/');
    }
  }, [navigate]);

  const handlePasswordChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setPassword(value);
      setError('');
    }
  };

  const handleSquareClick = (index) => {
    // Focus the hidden input when clicking on squares
    const hiddenInput = document.getElementById('password');
    if (hiddenInput) {
      hiddenInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || password.length !== 6) {
      setError('Please enter a valid 6-digit password');
      return;
    }

    if (!pendingLogin) {
      setError('Session expired. Please try again.');
      navigate('/');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Check attempt limit
      if (attempts >= 3) {
        setError('Too many failed attempts. Please try again later.');
        sessionStorage.removeItem('pendingLogin');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      // Verify password against user's stored password
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('password')
        .eq('user_id', pendingLogin.userData.user_id)
        .single();

      if (userError) {setError('An error occurred. Please try again.');
        return;
      }

      if (password === userData.password) {// Final subscription gate (in case it changed between ID lookup and password entry)
        const sub = evaluateSchoolSubscription(pendingLogin?.userData?.schools);
        if (!sub.isAccessible) {
          setError('Account Currently Unavailable');
          sessionStorage.removeItem('pendingLogin');
          setTimeout(() => navigate('/?error=account-unavailable'), 800);
          return;
        }

        // OIDC: if we were sent here by an OAuth partner (e.g. Everybody Counts), resume their flow
        const oidcStateId = sessionStorage.getItem('oidc_state_id');
        if (oidcStateId) {
          sessionStorage.removeItem('oidc_state_id');
          const apiBase = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/$/, '');
          if (apiBase) {
            try {
              const res = await fetch(`${apiBase}/oidc/resume`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  oidc_state_id: oidcStateId,
                  user_id: pendingLogin.userData.user_id,
                  password,
                }),
              });
              const data = await res.json().catch(() => ({}));
              if (res.ok && data.redirect_uri) {
                window.location.href = data.redirect_uri;
                return;
              }
            } catch (e) {
              console.error('OIDC resume failed', e);
            }
          }
        }

        // Store user data in sessionStorage for the providers page
        sessionStorage.setItem('currentUser', JSON.stringify(pendingLogin.userData));
        sessionStorage.removeItem('pendingLogin');

        // Persist teacher/student login for 1 week for faster future access.
        if (isTeacherOrStudentRole(pendingLogin?.userData?.role)) {
          savePersistedLogin(pendingLogin.userData);
        }

        setSuccess('Login successful! Redirecting...');

        // Navigate to providers page after a short delay
        setTimeout(() => {
          navigate('/home');
        }, 1500);

      } else {
        // Increment attempts
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        if (newAttempts >= 3) {
          setError('Too many failed attempts. Please try again later.');
          sessionStorage.removeItem('pendingLogin');
          setTimeout(() => navigate('/'), 2000);
        } else {
          setError('Incorrect password');
        }
      }
    } catch (err) {setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!pendingLogin) {
    return (
      <div className="password-page">
        <div className="password-loading">
          <div className="hero-loading-spinner">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
            </svg>
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="password-page">
      <div className="password-container">
        <div className="password-header">
          <div className="password-logo">
            <img src={Logo} alt="MyDigitalSchool Logo" className="password-logo-image" />
          </div>
          <h1 className="password-title">Enter Your Password</h1>
          <p className="password-subtitle">
            Please enter your 6-digit password for<br />
            <strong>{userEmail}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="password-form">
          <div className="password-input-group">
            <label htmlFor="password" className="password-label">Enter Your Password</label>

            {/* Modern 6-square input design */}
            <div className="password-squares-container">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div
                  key={index}
                  className={`password-square ${password.length > index ? 'filled' : ''} ${password.length === index ? 'active' : ''}`}
                  onClick={() => handleSquareClick(index)}
                >
                  {password[index] || ''}
                </div>
              ))}
            </div>

            {/* Hidden input for actual password entry */}
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              className="password-hidden-input"
              maxLength="6"
              disabled={isLoading}
              autoComplete="current-password"
              autoFocus
            />
          </div>

          {error && (
            <div className="password-error">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}

          {success && (
            <div className="password-success">
              <span className="success-icon">✅</span>
              <span className="success-text">{success}</span>
            </div>
          )}

          <div className="password-info">
            <span className="info-text">
              Enter the 6-digit password assigned to your account
            </span>
          </div>

          <button
            type="submit"
            className={`password-submit-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || password.length !== 6}
          >
            {isLoading ? (
              <>
                <div className="hero-loading-spinner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416">
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite" />
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>
                Verifying...
              </>
            ) : (
              'Verify & Continue'
            )}
          </button>

          <div className="password-actions">
            <button
              type="button"
              className="password-back-btn"
              onClick={() => navigate('/')}
              disabled={isLoading}
            >
              ← Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordVerification;
